// intro-page.component.ts

import {
  Component,
  AfterViewInit,
  OnDestroy,
  OnInit,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { auditTime, distinctUntilChanged } from 'rxjs/operators';
import { ScrollStateService } from '@services/scroll-state.service';

@Component({
  selector: 'app-intro-page',
  standalone: true,
  templateUrl: './intro-page.component.html',
  styleUrls: ['./intro-page.component.scss'],
})
export class IntroPageComponent implements OnInit, AfterViewInit, OnDestroy {
  atTop$!: Observable<boolean>;

  atTop = true;
  private sub?: Subscription;

  private destroyFns: Array<() => void> = [];
  private readonly VISIBLE = 2;

  private readonly isBrowser: boolean;

  hintText = '';

  constructor(
    @Inject(PLATFORM_ID) private pid: Object,
    private router: Router,
    private scrollState: ScrollStateService,
    @Inject(DOCUMENT) private doc: Document,
  ) {
    this.isBrowser = isPlatformBrowser(this.pid);
  }

  ngOnInit(): void {
    // SSR-safe: don’t subscribe to browser scroll state on the server.
    if (!this.isBrowser) {
      this.atTop = true;
      this.atTop$ = of(true);
      return;
    }

    // Make emissions async to avoid NG0100 (value flipping during Angular’s initial dev-mode checks).
    this.atTop$ = this.scrollState.atTop$.pipe(
      distinctUntilChanged(),
      auditTime(0),
    );

    this.sub = this.atTop$.subscribe((v) => (this.atTop = v));
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.refreshHint();

    const onResize = () => this.refreshHint();
    window.addEventListener('resize', onResize, { passive: true });
    this.destroyFns.push(() => window.removeEventListener('resize', onResize));

    this.attachTocObserver();

    const t = window.setTimeout(() => {
      this.refreshHint();
      this.setupCollapsers();
    }, 350);
    this.destroyFns.push(() => window.clearTimeout(t));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();

    this.destroyFns.forEach((fn) => fn());
    this.destroyFns = [];
  }

  onTitleClick(ev: Event): void {
    // Space key would otherwise scroll the page; prevent for both click + key activation.
    ev.preventDefault();

    if (this.atTop) return;
    this.scrollToTop(ev);
  }

  onExampleClick(ev?: Event): void {
    ev?.stopPropagation();
    if (!this.isBrowser) return;

    const biochem = this.pickRandomTocLinkByHint('biochem');
    if (biochem) {
      this.navigateToAnchor(biochem);
      return;
    }

    const any = this.pickRandomAnyTocLink();
    if (any) {
      this.navigateToAnchor(any);
      return;
    }

    void this.router.navigateByUrl('/step-1/cystic-fibrosis');
  }

  private scrollToTop(ev?: Event): void {
    if (!this.isBrowser) return;

    const header = (ev?.currentTarget as HTMLElement | null) ?? null;
    const scroller =
      this.getScrollParent(header) ??
      (this.doc.scrollingElement as HTMLElement | null);

    scroller?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private getScrollParent(el: HTMLElement | null): HTMLElement | null {
    if (!this.isBrowser) return null;

    let node: HTMLElement | null = el ? el.parentElement : null;
    while (node && node !== this.doc.body) {
      const s = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(s.overflowY)) return node;
      node = node.parentElement;
    }

    return this.doc.scrollingElement as HTMLElement | null;
  }

  private refreshHint(): void {
    if (!this.isBrowser) return;

    const isDesktop = window.matchMedia('(min-width: 801px)').matches;

    this.hintText = !isDesktop
      ? 'Tap the Browse button below to open the Table of Contents, or try an example to jump in.'
      : 'Start in the Table of Contents on the left, or try an example to jump in.';
  }

  /**
   * The sidebar outlet often renders AFTER this component.
   * Observe #toc-sheet and (re)apply collapsers whenever its DOM changes.
   */
  private attachTocObserver(): void {
    const tryAttach = (): boolean => {
      const toc = document.querySelector('#toc-sheet');
      if (!toc) return false;

      this.setupCollapsers();

      const obs = new MutationObserver(() => this.setupCollapsers());
      obs.observe(toc, { childList: true, subtree: true });
      this.destroyFns.push(() => obs.disconnect());

      return true;
    };

    if (tryAttach()) return;

    let tries = 0;
    const retry = () => {
      if (tryAttach()) return;

      tries += 1;
      if (tries >= 25) return;

      const t = window.setTimeout(retry, 120);
      this.destroyFns.push(() => window.clearTimeout(t));
    };

    retry();
  }

  private navigateToAnchor(a: HTMLAnchorElement): void {
    const href = (a.getAttribute('href') ?? '').trim();
    if (href === '') return;
    if (href.startsWith('#')) return;
    if (href.toLowerCase().startsWith('javascript:')) return;

    if (href.startsWith('/')) {
      void this.router.navigateByUrl(href);
      return;
    }

    a.click();
  }

  private pickRandomTocLinkByHint(hintLower: string): HTMLAnchorElement | null {
    const candidates = this.getTocAnchors().filter((a) => {
      const href = (a.getAttribute('href') ?? '').toLowerCase();
      return href.includes(hintLower);
    });

    if (candidates.length === 0) return null;
    return this.pickRandom(candidates);
  }

  private pickRandomAnyTocLink(): HTMLAnchorElement | null {
    const anchors = this.getTocAnchors();
    if (anchors.length === 0) return null;
    return this.pickRandom(anchors);
  }

  private getTocAnchors(): HTMLAnchorElement[] {
    const all = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('#toc-sheet a[href]'),
    );

    return all.filter((a) => {
      const href = (a.getAttribute('href') ?? '').trim();
      if (href === '') return false;
      if (href.startsWith('#')) return false;
      if (href.toLowerCase().startsWith('javascript:')) return false;

      const target = (a.getAttribute('target') ?? '').trim();
      const download = a.hasAttribute('download');

      if (
        href.startsWith('/') ||
        href.startsWith('./') ||
        href.startsWith('../')
      ) {
        return true;
      }

      return target === '' && !download;
    });
  }

  private pickRandom<T>(arr: T[]): T {
    const idx = Math.floor(Math.random() * arr.length);
    return arr[idx];
  }

  /**
   * Collapses long second-level lists in the TOC by hiding extra <li> elements directly.
   * This avoids relying on CSS scoped to this component.
   */
  private setupCollapsers(): void {
    if (!this.isBrowser) return;

    const uls = document.querySelectorAll<HTMLUListElement>(
      '#toc-sheet .next > li > ul',
    );

    uls.forEach((ul) => {
      if (ul.dataset['collapserSetup'] === '1') return;

      const items = Array.from(ul.children).filter(
        (n): n is HTMLLIElement => n.tagName === 'LI',
      );

      if (items.length <= this.VISIBLE) {
        ul.dataset['collapserSetup'] = '1';
        return;
      }

      const hideExtra = () => {
        items.forEach((li, idx) => {
          if (idx < this.VISIBLE) return;

          if (li.dataset['collapserOrigDisplay'] === undefined) {
            li.dataset['collapserOrigDisplay'] = li.style.display ?? '';
          }

          li.style.display = 'none';
        });
      };

      const showExtra = () => {
        items.forEach((li, idx) => {
          if (idx < this.VISIBLE) return;
          li.style.display = li.dataset['collapserOrigDisplay'] ?? '';
        });
      };

      const moreLi = document.createElement('li');
      moreLi.className = 'more';
      moreLi.style.listStyle = 'none';
      moreLi.style.marginTop = '4px';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'more…';
      btn.style.cursor = 'pointer';
      btn.style.color = 'rgba(255, 255, 255, .6)';
      btn.style.textDecoration = 'underline';
      btn.style.fontSize = 'clamp(0.5rem, 1.6vw, 0.8rem)';
      btn.style.border = 'none';
      btn.style.background = 'none';
      btn.style.padding = '0';
      btn.setAttribute('aria-expanded', 'false');

      moreLi.appendChild(btn);
      ul.appendChild(moreLi);

      hideExtra();

      const handler = () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !isExpanded;

        if (nextExpanded) {
          showExtra();
          btn.textContent = 'less';
        } else {
          hideExtra();
          btn.textContent = 'more…';
        }

        btn.setAttribute('aria-expanded', String(nextExpanded));
      };

      btn.addEventListener('click', handler);
      this.destroyFns.push(() => btn.removeEventListener('click', handler));

      ul.dataset['collapserSetup'] = '1';
    });
  }
}
